import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { ErrorTextField } from "@/components/atoms";
import { useAudienceContext } from "@/context";

interface CreateAudienceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (audienceName: string, projectName: string | null) => void;
}

const CreateAudienceDialog: React.FC<CreateAudienceDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const { state } = useAudienceContext();
  const [audienceName, setAudienceName] = useState<string>("");
  const [projectName, setProjectName] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  const projects = useMemo(
    () =>
      Array.from(
        new Set(
          state.audiences
            ?.filter((a) => a.project_name)
            .map((a) => a.project_name as string)
            .sort((a, b) => a.localeCompare(b)),
        ),
      ),
    [state.audiences],
  );

  useEffect(() => {
    state.audiences?.find((audience) => audience.audience_name === audienceName)
      ? setError(true)
      : setError(false);
  }, [audienceName, state]);

  const handleAudienceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(false);
    setAudienceName(e.target.value);
  };

  const handleProjectNameChange = (
    _event: React.SyntheticEvent,
    value: string | null,
  ) => {
    setProjectName(value);
  };

  const handleProjectInputChange = (
    _event: React.SyntheticEvent,
    value: string,
  ) => {
    setProjectName(value.trim());
  };

  const handleSaveAudience = () => {
    onConfirm(
      audienceName.trim(),
      projectName && projectName !== "" ? projectName.trim() : null,
    );
  };

  return (
    <Dialog open={open} disableEnforceFocus aria-labelledby="save-dialog">
      <DialogTitle>
        <Typography>New Audience</Typography>
      </DialogTitle>
      <DialogContent sx={{ width: 300 }}>
        <Stack spacing={2}>
          <Stack>
            <ErrorTextField
              error={error}
              variant="outlined"
              label="Audience Name"
              size="small"
              color="primary"
              placeholder="Audience Name"
              value={audienceName}
              onChange={handleAudienceNameChange}
              sx={{ mt: 1 }}
            />
            {error && (
              <Typography variant="caption" color="error">
                Audience name already exists
              </Typography>
            )}
          </Stack>
          <Autocomplete
            freeSolo
            value={projectName}
            onChange={handleProjectNameChange}
            inputValue={projectName ?? ""}
            onInputChange={handleProjectInputChange}
            options={projects}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : ""
            }
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Project Name (Optional)"
                size="small"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveAudience}
          disabled={error}
        >
          Save Audience
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAudienceDialog;
